import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import tableDataType, { tableRowsType, colTitlesType } from '../proptypes/table-data';
import { mediaBreakpointDownMD, borderStyle } from '../constants/style-constants';
import { Subtext } from './Typography';

export const TableTitleDiv = styled.div`
  display: none;
  width: auto;

  ${mediaBreakpointDownMD} {
    width: 50%;
    display: block;
    font-weight: bold;
  }
`;

export const TableStatDiv = styled.div`
  width: auto;

  ${mediaBreakpointDownMD} {
    width: 50%;
  }
`;

const tableCss = css`
  tr {
    border-left: ${borderStyle};
    border-right: ${borderStyle};

    &:last-child {
      border-bottom: ${borderStyle};
    }

    ${mediaBreakpointDownMD} {
      border-bottom: ${borderStyle};
      display: block;
      margin: 0 0 2rem;
    }
  }

  th {
    border-top: ${borderStyle};
    width: 33%;

    ${mediaBreakpointDownMD} {
      width: unset;
      display: block;
    }
  }

  td {
    border-top: ${borderStyle};
    width: 33%;

    ${mediaBreakpointDownMD} {
      width: unset;
      display: flex;
      flex-wrap: nowrap;
    }
  }

  thead {
    display: table-header-group;

    ${mediaBreakpointDownMD} {
      display: none;
    }

    th {
      border-top: ${borderStyle};
      border-bottom: ${borderStyle};
    }
  }
`;

const defaultCellRender = (cell, colTitle, cellIndex) => (
  <td key={`${colTitle}_${cellIndex}`}>
    <TableTitleDiv>{colTitle}</TableTitleDiv>
    <TableStatDiv>{cell}</TableStatDiv>
  </td>
);

const TableHead = ({ colTitles }) => (
  <thead>
    <tr>
      <th scope="col">{/* empty cell */}</th>
      { colTitles.map(title => <th scope="col" key={title}>{title}</th>) }
    </tr>
  </thead>
);

TableHead.propTypes = {
  colTitles: colTitlesType.isRequired,
};

const TableBody = ({ rows, colTitles, customCellRender }) => (
  <tbody>
    {
      rows.map(({ cells, rowTitle, rowSubtitle }) => (
        <tr key={`${rowTitle}-${rowSubtitle}`}>
          <th scope="row">
            <div>{rowTitle}</div>
            {
              rowSubtitle && <Subtext>{rowSubtitle}</Subtext>
            }
          </th>
          {
            cells.map((cell, index) => (
              customCellRender(cell, colTitles[index], index)
              || defaultCellRender(cell, colTitles[index], index)
            ))
          }
        </tr>
      ))
    }
  </tbody>
);

TableBody.propTypes = {
  rows: tableRowsType.isRequired,
  colTitles: colTitlesType.isRequired,
  customCellRender: PropTypes.func.isRequired,
};

const Table = ({
  customCellRender,
  tableHead,
  data: {
    colTitles,
    rows,
  },
}) => (
  <table className="table table-hover" css={tableCss}>
    { tableHead && <TableHead {...{ colTitles }} /> }
    <TableBody {...{ rows, colTitles, customCellRender }} />
  </table>
);

Table.defaultProps = {
  customCellRender: () => undefined,
  tableHead: true,
};

Table.propTypes = {
  tableHead: PropTypes.bool,
  customCellRender: PropTypes.func,
  data: tableDataType.isRequired,
};

export default Table;
